// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getCssUnitField from "@store/Framework/Common/Fields/Toolkit/cssUnit";
import { ResponsiveButtonPresentationProps } from "@store/Library/Components/Toolkit/ResponsiveButton";
import getButtonSizeVariantField from "@store/Framework/Common/Fields/Toolkit/buttonSizeVariant";
import getButtonVariantField from "@store/Framework/Common/Fields/Toolkit/buttonVariant";
import { ButtonSizeVariants } from "@store/MobiusOverrides/Button/Button";
import { getGridPreset } from "@toolkit/Framework/Common/Utilities/gridPreset";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { spacingStyles } from "@toolkit/Themes/common/mixins";
import PreTitle from "@store/Library/Components/PreTitle/PreTitle";
import * as cssLinter from "css";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import OurOfferingTile from "@store/Library/Components/OurOffering/OurOfferingTile";
import { LazyImageProps } from "@store/MobiusOverrides/LazyImage/LazyImage";
import { useSession } from "@toolkit/Framework/Store/Context/ContextHooks";

const enum fields {
    preTitle = "preTitle",
    title = "title",
    gap = "gap",
    tiles = "tiles",
    customCSS = "customCSS",
}

const enum tileFields {
    id = "id",
    customerTypeRestriction = "customerTypeRestriction",
    title = "title",
    description = "description",
    image = "image",
    ctaText = "ctaText",
    ctaLink = "ctaLink",
    ctaVariant = "ctaVariant",
    ctaSizeVariant = "ctaSizeVariant",
    ctaAlignment = "ctaAlignment",
    ctaFullWidth = "ctaFullWidth",
}

export type CustomerType = "valves" | "krebs express pumps" | "doe pumps" | "cyclones";
export type CustomerTypeRestriction = "All" | CustomerType;

const getCustomerTypes = (raw?: string | null): CustomerType[] => {
    if (!raw) return [];
    return JSON.parse(raw) as CustomerType[];
};

const gapField = getCssUnitField(fields.gap, { shorthands: ["row", "column"] });
export const ctaVariantField = getButtonVariantField(tileFields.ctaVariant);
export const ctaSizeVariantField = getButtonSizeVariantField(tileFields.ctaSizeVariant);

export interface OurOfferingTileFields {
    [tileFields.id]: string;
    [tileFields.customerTypeRestriction]: CustomerTypeRestriction;
    [tileFields.title]: string;
    [tileFields.description]: string;
    [tileFields.image]: string;
    [tileFields.ctaText]: string;
    [tileFields.ctaLink]: LinkFieldValue;
    [tileFields.ctaVariant]: "primary" | "secondary" | "tertiary" | "inverted" | "invertedOutline";
    [tileFields.ctaSizeVariant]: ButtonSizeVariants;
    [tileFields.ctaAlignment]: "flex-start" | "center" | "flex-end";
    [tileFields.ctaFullWidth]: boolean;
}

interface OurOfferingWidgetProps extends WidgetProps {
    fields: {
        [fields.preTitle]: string;
        [fields.title]: string;
        [fields.gap]: string;
        [fields.customCSS]: string;
        [fields.tiles]: { fields: OurOfferingTileFields }[];
    };
}

export interface OurOfferingStyles {
    wrapper?: InjectableCss;
    header?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    gridContainer?: GridContainerProps;
    gridItem?: GridItemProps;
    tile?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    body?: InjectableCss;
    tileTitle?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    ctaRow?: InjectableCss;
    cta?: ResponsiveButtonPresentationProps;
}

export const ourOfferingStyles: OurOfferingStyles = {
    wrapper: {
        css: spacingStyles(),
    },
    header: {
        css: css`
            display: grid;
            row-gap: ${pxToRem(8)};
            margin-bottom: ${pxToRem(24)};

            ${breakpoint("md")} {
                row-gap: ${pxToRem(10)};
                margin-bottom: ${pxToRem(32)};
            }
        `,
    },
    sectionTitle: {
        variant: "h2",
        css: css`
            margin: 0;
        `,
    },
    gridItem: {
        width: [12, 6, 6, 6, 6],
    },
    tile: {
        css: css`
            overflow: hidden;
            display: flex;
            flex-direction: column;
            width: 100%;
            min-height: 100%;
        `,
    },
    imageWrapper: {
        css: css`
            width: 100%;
            height: ${pxToRem(250)};
            border-radius: ${pxToRem(8)};
            overflow: hidden;
            background: rgba(255, 255, 255, 0.1);

            ${breakpoint("sm")} {
                height: ${pxToRem(405)};
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: 100%;
            display: block;

            img {
                width: 100%;
                height: 100%;
                display: block;
                object-fit: contain;
            }
        `,
    },
    body: {
        css: css`
            padding: ${pxToRem(20, 0)};
            display: grid;
            row-gap: ${pxToRem(10)};

            ${breakpoint("sm")} {
                padding: ${pxToRem(20, 0)};
            }
        `,
    },
    tileTitle: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    description: {
        variant: "p",
        css: css`
            margin: 0;

            display: -webkit-box;
            -webkit-line-clamp: 3;
            -webkit-box-orient: vertical;
            overflow: hidden;
        `,
    },
    ctaRow: {
        css: css`
            display: flex;
            width: 100%;
            margin-top: ${pxToRem(6)};
        `,
    },
    cta: {
        css: css`
            width: auto;
            display: inline-flex;
        `,
        mergeCss: true,
    },
};

export const OurOfferingWidget: FC<OurOfferingWidgetProps> = ({
    fields: { preTitle, title, gap, tiles, customCSS },
}) => {
    const styles = useMergeStyles("ourOffering" as any, ourOfferingStyles);
    const { isEditing } = useShellContext();
    const session = useSession();

    const allowedTypes = useMemo(() => {
        return getCustomerTypes(session?.properties?.customerTypes);
    }, [session?.properties?.customerTypes]);

    const visibleTiles = useMemo(() => {
        if (!tiles?.length) return [];
        if (isEditing) return tiles;

        const isTileVisible = (restriction: CustomerTypeRestriction) =>
            restriction === "All" || allowedTypes.includes(restriction);

        return tiles.filter(x => isTileVisible(x.fields.customerTypeRestriction));
    }, [allowedTypes, isEditing, tiles]);

    const wrapperStyles = useMemo(
        () => ({
            ...styles.wrapper,
            css: css`
                ${styles.wrapper?.css}
                ${customCSS}
            `,
        }),
        [customCSS, styles.wrapper],
    );

    const gridContainerStyles = useMemo(() => {
        const gridPreset = gapField.parseToGridPreset(gap);

        return mergeProps<GridContainerProps>(
            styles.gridContainer,
            mergeProps<GridContainerProps>(gridPreset ? getGridPreset(gridPreset) : undefined, { gap: 0 }),
        );
    }, [gap, styles.gridContainer]);

    const gridItemStyles = useMemo(() => {
        return mergeProps<GridItemProps>(styles.gridItem, {});
    }, [styles.gridItem]);

    if (!visibleTiles?.length && !title && !preTitle) {
        return null;
    }

    return (
        <StyledWrapper className="wrapper" {...wrapperStyles}>
            <StyledWrapper className="header" {...styles.header}>
                <PreTitle text={preTitle} className="preTitle" />
                {!!title && (
                    <Typography className="title" {...styles.sectionTitle}>
                        {title}
                    </Typography>
                )}
            </StyledWrapper>

            {!!visibleTiles?.length && (
                <StyledWrapper className="tiles">
                    <GridContainer {...gridContainerStyles}>
                        {visibleTiles.map(({ fields: tile }, index) => (
                            <OurOfferingTile
                                key={tile.id || `${index}`}
                                tile={tile}
                                index={index}
                                styles={styles}
                                gridItemStyles={gridItemStyles}
                            />
                        ))}
                    </GridContainer>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const defaultCustomCss = `& {
}

.header {
}

.preTitle {
}

.title {
}

.tiles {
}

.tile {
}

.tileImageWrapper {
}

.tileImage {
}

.tileBody {
}

.tileTitle {
}

.tileDescription {
}

.tileCtaRow {
}

.tileCta {
}
`;

const basicTab = { displayName: "Basic", sortOrder: 0 };
const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: OurOfferingWidget,
    definition: {
        displayName: "Our Offering",
        group: "Basic",
        icon: "th-large",
        fieldDefinitions: [
            {
                name: fields.preTitle,
                displayName: "Pre-title",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
                tab: basicTab,
            },
            {
                ...gapField.definition,
                displayName: "Gap Between Tiles",
                fieldType: "Contextual",
                defaultValue: "32px",
                tab: basicTab,
            },
            {
                name: fields.tiles,
                displayName: "Tiles",
                editorTemplate: "ListField",
                fieldType: "Contextual",
                defaultValue: [],
                tab: basicTab,
                getDisplay: x => x.fields.title || "Tile",
                fieldDefinitions: [
                    { name: tileFields.id, editorTemplate: "UniqueIdField" },
                    {
                        name: tileFields.customerTypeRestriction,
                        displayName: "Customer type restriction",
                        tooltip: "Controls which users can see this tile based on their customer types.",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Show for all customer types", value: "All" },
                            { displayName: "Valve customers", value: "valves" },
                            { displayName: "Krebs Express pump customers", value: "krebs express pumps" },
                            { displayName: "DOE pump customers", value: "doe pumps" },
                            { displayName: "Cyclone customers", value: "cyclones" },
                        ],
                        defaultValue: "All",
                        hideEmptyOption: true,
                    },
                    {
                        name: tileFields.image,
                        displayName: "Image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    { name: tileFields.title, displayName: "Title", editorTemplate: "TextField", defaultValue: "" },
                    {
                        name: tileFields.description,
                        displayName: "Description",
                        editorTemplate: "MultilineTextField",
                        defaultValue: "",
                    },
                    {
                        name: tileFields.ctaText,
                        displayName: "CTA Text",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: tileFields.ctaLink,
                        displayName: "CTA Link",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    { ...ctaVariantField.definition },
                    { ...ctaSizeVariantField.definition },
                    {
                        name: tileFields.ctaAlignment,
                        displayName: "CTA Alignment",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Left", value: "flex-start" },
                            { displayName: "Center", value: "center" },
                            { displayName: "Right", value: "flex-end" },
                        ],
                        defaultValue: "flex-start",
                        hideEmptyOption: true,
                    },
                    {
                        name: tileFields.ctaFullWidth,
                        displayName: "CTA Full Width",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) return "";
                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors?.length) {
                        return result.stylesheet.parsingErrors.map(e => `${e.reason} on line ${e.line}`)[0];
                    }
                    return "";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
